import { useState } from 'react';
import { Store, MapPin, Truck, Package } from 'lucide-react';
import { OrderSummary } from './OrderSummary';
import { CartListItem } from './CartListItem';
import { useNavigate } from 'react-router';
import type { Location, Maybe, OrderItem } from '~/graphql/graphql';
import { useTranslation } from 'react-i18next';
import { calcItemLoads } from '~/utils/order';

interface CartCardProps {
  sellingOrganizationId: string;
  supplierName: string;
  supplierLocation: string;
  items?: OrderItem[];
  id: string;
  shippingLocation?: Maybe<Location>;
}

export function CartCard({
  sellingOrganizationId,
  supplierName,
  supplierLocation,
  items = [],
  id,
  shippingLocation,
}: Readonly<CartCardProps>) {
  const { t } = useTranslation('cart');
  const [selectedAddressId, setSelectedAddressId] = useState(
    shippingLocation?.id || ''
  );

  const handleAddressChange = (addressId: string) => {
    setSelectedAddressId(addressId);
  };

  const navigate = useNavigate();

  const handleCheckout = () => {
    navigate(`/cart/${id}/checkout/address/${selectedAddressId}`);
  };

  const subtotal = items.reduce(
    (sum, item) => sum + item.quantity.value * item.price,
    0
  );
  const itemCount = items.length;

  const { truckLoads, railcarLoads } = items.reduce(
    (acc, item) => {
      const loads = calcItemLoads(item);
      return {
        truckLoads: acc.truckLoads + loads.truckLoads,
        railcarLoads: acc.railcarLoads + loads.railcarLoads,
      };
    },
    { truckLoads: 0, railcarLoads: 0 }
  );

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-[400px]">
        <div className="lg:col-span-2 flex flex-col">
          <div className="p-4 sm:p-6 border-b border-neutral-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Store className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-neut-prim font-semibold">{supplierName}</h3>
                <div className="flex items-center gap-1 text-xs text-neut-sec mt-0.5">
                  <MapPin className="w-3 h-3" />
                  <span>{supplierLocation}</span>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex items-center gap-1 px-2 py-1 bg-status-info-50 rounded-md">
                    <Truck className="w-3 h-3 text-status-info-600" />
                    <span className="text-xs font-semibold text-status-info-700">
                      {truckLoads.toFixed(2)}
                    </span>
                    <span className="text-xs text-status-info-600">
                      {t('cart.trucks_lowercase')}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-warning-50 rounded-md">
                    <Package className="w-3 h-3 text-warning-600" />
                    <span className="text-xs font-semibold text-warning-700">
                      {railcarLoads.toFixed(2)}
                    </span>
                    <span className="text-xs text-warning-600">
                      {t('cart.railcars_lowercase')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1" data-testid="cart-items">
            {items.map((item) => (
              <CartListItem
                key={item?.platformProductsLengths?.id}
                item={item}
                cartId={id}
                sellingOrganizationId={sellingOrganizationId}
                shippingLocation={shippingLocation}
              />
            ))}
          </div>
        </div>

        <OrderSummary
          selectedAddressId={selectedAddressId}
          onAddressChange={handleAddressChange}
          subtotal={subtotal}
          itemCount={itemCount}
          shippingFee="TBD"
          total={subtotal}
          onCheckout={handleCheckout}
          isPlaceOrderDisabled={!selectedAddressId}
        />
      </div>
    </div>
  );
}
